import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext'; // Import the UserContext
import api from '../services/api'; // Importa o serviço de API configurado
import RewardAnimation from './RewardAnimation'; // Importa o componente de animação de recompensa

// Componente para exibir um flashcard
const Flashcard = ({ flashcard, onEditar, onArquivar }) => {
    const [virado, setVirado] = useState(false);
    const [editando, setEditando] = useState(false);
    const [frente, setFrente] = useState(flashcard.frente);
    const [verso, setVerso] = useState(flashcard.verso);
    const maxLength = 150; // Maximum length for front and back
    const lineBreakInterval = 50; // Break line after every 50 characters

    // Função para adicionar quebras de linha automaticamente
    const addLineBreaks = (text) => {
        if (!text) return '';

        let result = '';
        for (let i = 0; i < text.length; i += lineBreakInterval) {
            if (i > 0) {
                result += '\n';
            }
            result += text.substr(i, lineBreakInterval);
        }
        return result;
    };

    // Função para lidar com a mudança no campo frente
    const handleFrenteChange = (e) => {
        let value = e.target.value;
        if (value.length <= maxLength) {
            // Remover quebras de linha existentes para contar corretamente
            value = value.replace(/\n/g, '');

            // Adicionar quebras de linha automaticamente a cada 50 caracteres
            let formattedValue = '';
            for (let i = 0; i < value.length; i += lineBreakInterval) {
                if (i > 0) {
                    formattedValue += '\n';
                }
                formattedValue += value.substr(i, lineBreakInterval);
            }

            setFrente(formattedValue);
        }
    };

    // Função para lidar com a mudança no campo verso
    const handleVersoChange = (e) => {
        let value = e.target.value;
        if (value.length <= maxLength) {
            // Remover quebras de linha existentes para contar corretamente
            value = value.replace(/\n/g, '');

            // Adicionar quebras de linha automaticamente a cada 50 caracteres
            let formattedValue = '';
            for (let i = 0; i < value.length; i += lineBreakInterval) {
                if (i > 0) {
                    formattedValue += '\n';
                }
                formattedValue += value.substr(i, lineBreakInterval);
            }

            setVerso(formattedValue);
        }
    };

    const handleSalvar = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo'));
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userInfo.token}`
                }
            };

            const data = {
                frente,
                verso
            };

            await api.put(`/api/flashcards/${flashcard._id}`, data, config);
            onEditar(flashcard._id, { frente, verso });
            setEditando(false);
        } catch (error) {
            console.error('Erro ao atualizar flashcard:', error);
        }
    };

    const handleArquivar = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo'));
            const config = {
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`
                }
            };

            await api.delete(`/api/flashcards/${flashcard._id}`, config);
            onArquivar(flashcard._id);
        } catch (error) {
            console.error('Erro ao arquivar flashcard:', error);
        }
    };

    if (editando) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-md border border-[#E0CCFF] mb-4 transition-all duration-300 hover:shadow-lg">
                <div className="relative">
                    <textarea
                        value={frente}
                        onChange={handleFrenteChange}
                        className="w-full p-2 mb-1 border rounded-lg"
                        placeholder="Frente do flashcard"
                        rows="3"
                    />
                    <div className={`text-xs mt-1 text-right ${frente.length === maxLength ? 'text-red-500' : 'text-gray-500'}`}>
                        {frente.length}/{maxLength}
                    </div>
                </div>
                <div className="relative mt-2">
                    <textarea
                        value={verso}
                        onChange={handleVersoChange}
                        className="w-full p-2 mb-1 border rounded-lg"
                        placeholder="Verso do flashcard"
                        rows="3"
                    />
                    <div className={`text-xs mt-1 text-right ${verso.length === maxLength ? 'text-red-500' : 'text-gray-500'}`}>
                        {verso.length}/{maxLength}
                    </div>
                </div>
                <div className="flex justify-end space-x-2 mt-3">
                    <button
                        onClick={() => {
                            setEditando(false);
                            setFrente(flashcard.frente);
                            setVerso(flashcard.verso);
                        }}
                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSalvar}
                        disabled={frente.length > maxLength || verso.length > maxLength || !frente.trim() || !verso.trim()}
                        className={`px-3 py-1 text-sm rounded-lg ${
                            frente.length <= maxLength && verso.length <= maxLength && frente.trim() && verso.trim()
                                ? 'bg-violet-600 text-white hover:bg-violet-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        Salvar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`bg-white p-4 rounded-lg shadow-md border border-[#E0CCFF] mb-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                virado ? 'bg-violet-50' : ''
            }`}
            onClick={() => setVirado(!virado)}
        >
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    {virado ? (
                        <p className="text-gray-700">{verso}</p>
                    ) : (
                        <p className="font-semibold text-gray-800">{frente}</p>
                    )}
                </div>
                <div className="flex space-x-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditando(true);
                        }}
                        className="p-1 text-gray-500 hover:text-violet-600"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleArquivar();
                        }}
                        className="p-1 text-gray-500 hover:text-red-600"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                    </button>
                </div>
            </div>
            <div className="mt-2 text-xs text-gray-500 text-center">
                {virado ? 'Clique para ver a frente' : 'Clique para ver o verso'}
            </div>
        </div>
    );
};

// Componente para criar um novo flashcard
const CriarFlashcard = ({ subtopicoId, videoaulaId, onCriar }) => {
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [frente, setFrente] = useState('');
    const [verso, setVerso] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Estado para controlar o carregamento
    const maxLength = 150; // Maximum length for front and back
    const lineBreakInterval = 50; // Break line after every 50 characters

    // Função para adicionar quebras de linha automaticamente
    const addLineBreaks = (text) => {
        if (!text) return '';

        let result = '';
        for (let i = 0; i < text.length; i += lineBreakInterval) {
            if (i > 0) {
                result += '\n';
            }
            result += text.substr(i, lineBreakInterval);
        }
        return result;
    };

    // Função para lidar com a mudança no campo frente
    const handleFrenteChange = (e) => {
        let value = e.target.value;
        if (value.length <= maxLength) {
            // Remover quebras de linha existentes para contar corretamente
            value = value.replace(/\n/g, '');

            // Adicionar quebras de linha automaticamente a cada 50 caracteres
            let formattedValue = '';
            for (let i = 0; i < value.length; i += lineBreakInterval) {
                if (i > 0) {
                    formattedValue += '\n';
                }
                formattedValue += value.substr(i, lineBreakInterval);
            }

            setFrente(formattedValue);
        }
    };

    // Função para lidar com a mudança no campo verso
    const handleVersoChange = (e) => {
        let value = e.target.value;
        if (value.length <= maxLength) {
            // Remover quebras de linha existentes para contar corretamente
            value = value.replace(/\n/g, '');

            // Adicionar quebras de linha automaticamente a cada 50 caracteres
            let formattedValue = '';
            for (let i = 0; i < value.length; i += lineBreakInterval) {
                if (i > 0) {
                    formattedValue += '\n';
                }
                formattedValue += value.substr(i, lineBreakInterval);
            }

            setVerso(formattedValue);
        }
    };

    const handleCriar = async () => {
        if (!frente.trim() || !verso.trim()) return;

        setIsLoading(true); // Iniciar o estado de carregamento

        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo'));
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userInfo.token}`
                }
            };

            const data = {
                subtopicoId,
                frente,
                verso,
                videoaulaId: videoaulaId || null  // Passa o ID da videoaula se estiver disponível
            };

            const response = await api.post('/api/flashcards', data, config);
            onCriar(response.data.flashcard);

            // Limpar formulário
            setFrente('');
            setVerso('');
            setMostrarFormulario(false);
        } catch (error) {
            console.error('Erro ao criar flashcard:', error);

            // Verificar se é erro de limite de flashcard por videoaula
            if (error.response && error.response.data && error.response.data.message) {
                if (error.response.data.message.includes('Já existe um flashcard criado para esta videoaula')) {
                    alert('Já existe um flashcard criado para esta videoaula. Você só pode criar um flashcard por videoaula.');
                } else {
                    console.error('Erro ao criar flashcard:', error.response.data.message);
                }
            }
        } finally {
            setIsLoading(false); // Finalizar o estado de carregamento
        }
    };

    if (!mostrarFormulario) {
        return (
            <div className="bg-violet-50 p-4 rounded-lg border border-violet-200 mb-6 transition-all duration-300 hover:shadow-lg">
                <button
                    onClick={() => setMostrarFormulario(true)}
                    className="flex items-center text-violet-700 font-semibold hover:text-violet-800"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Adicionar novo flashcard
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white p-4 rounded-lg shadow-md border border-[#E0CCFF] mb-6 transition-all duration-300 hover:shadow-lg relative">
            {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg z-10">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-2"></div>
                        <p className="text-violet-600 font-medium">Criando flashcard...</p>
                    </div>
                </div>
            )}
            <h3 className="font-semibold text-gray-800 mb-3">Criar novo flashcard</h3>
            <div className="relative">
                <textarea
                    value={frente}
                    onChange={handleFrenteChange}
                    className="w-full p-2 mb-1 border rounded-lg"
                    placeholder="Frente do flashcard (termo, pergunta, etc.)"
                    rows="3"
                    disabled={isLoading}
                />
                <div className={`text-xs mt-1 text-right ${frente.length === maxLength ? 'text-red-500' : 'text-gray-500'}`}>
                    {frente.length}/{maxLength}
                </div>
            </div>
            <div className="relative mt-2">
                <textarea
                    value={verso}
                    onChange={handleVersoChange}
                    className="w-full p-2 mb-1 border rounded-lg"
                    placeholder="Verso do flashcard (definição, resposta, etc.)"
                    rows="3"
                    disabled={isLoading}
                />
                <div className={`text-xs mt-1 text-right ${verso.length === maxLength ? 'text-red-500' : 'text-gray-500'}`}>
                    {verso.length}/{maxLength}
                </div>
            </div>
            <div className="flex justify-end space-x-2 mt-3">
                <button
                    onClick={() => {
                        setMostrarFormulario(false);
                        setFrente('');
                        setVerso('');
                    }}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    disabled={isLoading}
                >
                    Cancelar
                </button>
                <button
                    onClick={handleCriar}
                    disabled={frente.length > maxLength || verso.length > maxLength || !frente.trim() || !verso.trim() || isLoading}
                    className={`px-3 py-1 text-sm rounded-lg ${
                        frente.length <= maxLength && verso.length <= maxLength && frente.trim() && verso.trim() && !isLoading
                            ? 'bg-violet-600 text-white hover:bg-violet-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                    {isLoading ? 'Criando...' : 'Criar'}
                </button>
            </div>
        </div>
    );
};

// Componente para exibir os flashcards de um subtópico
const FlashcardsSubtopico = ({ subtopicoId, videoaulaId }) => {
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFlashcards = async () => {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const config = {
                    headers: {
                        'Authorization': `Bearer ${userInfo.token}`
                    }
                };

                const response = await api.get(`/api/flashcards/subtopico/${subtopicoId}`, config);
                setFlashcards(response.data.flashcards);
            } catch (error) {
                console.error('Erro ao buscar flashcards:', error);
            } finally {
                setLoading(false);
            }
        };

        if (subtopicoId) {
            fetchFlashcards();
        }
    }, [subtopicoId]);

    const handleCriar = (novoFlashcard) => {
        setFlashcards([novoFlashcard, ...flashcards]);
    };

    const handleEditar = (id, dadosAtualizados) => {
        setFlashcards(flashcards.map(f =>
            f._id === id ? { ...f, ...dadosAtualizados } : f
        ));
    };

    const handleArquivar = (id) => {
        setFlashcards(flashcards.filter(f => f._id !== id));
    };

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-6 transition-all duration-300 hover:shadow-lg">
                <p className="text-gray-600">Carregando flashcards...</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-6 transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Flashcards</h3>

            <CriarFlashcard subtopicoId={subtopicoId} videoaulaId={videoaulaId} onCriar={handleCriar} />

            {flashcards.length > 0 ? (
                <div>
                    <p className="text-gray-600 mb-4">
                        Clique nos flashcards para alternar entre frente e verso:
                    </p>
                    {flashcards.map(flashcard => (
                        <Flashcard
                            key={flashcard._id}
                            flashcard={flashcard}
                            onEditar={handleEditar}
                            onArquivar={handleArquivar}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-8">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                    </svg>
                    <p className="text-gray-500">
                        Nenhum flashcard criado ainda. Crie seu primeiro flashcard para ajudar na memorização!
                    </p>
                </div>
            )}
        </div>
    );
};

// Componente para um único exercício
const Exercicio = ({ exercicio, numero, token, onRespostaRegistrada }) => {
    const [respostaSelecionada, setRespostaSelecionada] = useState(null);
    const [resultado, setResultado] = useState(''); // '', 'correto', 'incorreto'
    const [feedbackMessage, setFeedbackMessage] = useState(''); // Mensagem de feedback para o usuário
    const [isSubmitting, setIsSubmitting] = useState(false); // Para controlar o estado de envio
    const [respostaAnterior, setRespostaAnterior] = useState(null); // Estado para armazenar resposta anterior
    const [mostrarAnimacao, setMostrarAnimacao] = useState(false); // Estado para controlar a animação de recompensa

    // Efeito para verificar se o usuário já respondeu esta questão
    useEffect(() => {
        const verificarRespostaAnterior = async () => {
            try {
                const config = {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                };

                // Busca respostas anteriores para este subtópico
                const response = await api.get(`/api/user-answers/subtopico/${exercicio.subtopico}`, config);
                
                // Verifica se há resposta para esta questão específica
                const resposta = response.data.respostasPorQuestao[exercicio._id];
                if (resposta) {
                    setRespostaAnterior(resposta);
                    setRespostaSelecionada(resposta.alternativaSelecionada);
                    setResultado(resposta.estaCorreta ? 'correto' : 'incorreto');
                    
                    if (resposta.estaCorreta) {
                        setFeedbackMessage('Resposta correta! 🎉 (Respondida anteriormente)');
                    } else {
                        // Usa a resposta correta armazenada no momento da submissão
                        const respostaCorreta = resposta.respostaCorretaNoMomento !== undefined ? 
                            resposta.respostaCorretaNoMomento : exercicio.respostaCorreta;
                        setFeedbackMessage(`Resposta incorreta. A correta era a alternativa ${String.fromCharCode(65 + respostaCorreta)}. (Respondida anteriormente)`);
                    }
                }
            } catch (error) {
                console.error('Erro ao verificar resposta anterior:', error);
            }
        };

        if (token && exercicio._id && exercicio.subtopico) {
            verificarRespostaAnterior();
        }
    }, [token, exercicio._id, exercicio.subtopico, exercicio.respostaCorreta]);

    const handleCheckResposta = async () => { // Função agora assíncrona
        if (respostaSelecionada === null || isSubmitting) return; // Impede múltiplos envios

        setIsSubmitting(true);
        setFeedbackMessage(''); // Limpa mensagens anteriores
        setMostrarAnimacao(false); // Esconde a animação antes de verificar

        try {
            // Prepara os dados para enviar ao backend
            const answerData = {
                questaoId: exercicio._id,
                alternativaSelecionada: respostaSelecionada,
            };

            // Configura o cabeçalho de autorização
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` // Usa o token passado via props
                }
            };

            // Faz a requisição POST para registrar a resposta
            const response = await api.post('/api/user-answers', answerData, config);

            // Verifica a resposta do backend
            if (response.data.estaCorreta) {
                setResultado('correto');
                setFeedbackMessage('Resposta correta! 🎉');
                setMostrarAnimacao(true); // Mostra a animação de recompensa
            } else {
                setResultado('incorreto');
                // Usa a resposta correta retornada pelo backend
                const respostaCorreta = response.data.respostaCorreta;
                setFeedbackMessage(`Resposta incorreta. A correta era a alternativa ${String.fromCharCode(65 + respostaCorreta)}.`);
            }

            // Notifica o componente pai que uma resposta foi registrada
            if (onRespostaRegistrada) {
                onRespostaRegistrada(exercicio._id, response.data);
            }
        } catch (error) {
            console.error('Erro ao registrar resposta:', error);
            setResultado(''); // Limpa o resultado se houver erro
            setFeedbackMessage('Ocorreu um erro ao verificar a resposta. Tente novamente.');
        } finally {
            setIsSubmitting(false); // Finaliza o estado de envio
        }
    };

    const getAlternativaClass = (index) => {
        if (!resultado) return "hover:bg-violet-50"; // Estilo normal antes de verificar

        // Se já temos o resultado, usamos a resposta correta armazenada
        let respostaCorreta = exercicio.respostaCorreta;
        
        // Se já respondeu e temos a resposta correta no momento da submissão, usamos ela
        if (resultado && respostaAnterior && respostaAnterior.respostaCorretaNoMomento !== undefined) {
            respostaCorreta = respostaAnterior.respostaCorretaNoMomento;
        }

        const isCorreta = index === respostaCorreta;
        const isSelecionada = index === respostaSelecionada;

        if (isCorreta) return "bg-green-100 border-green-500"; // Resposta correta é sempre verde
        if (isSelecionada && !isCorreta) return "bg-red-100 border-red-500"; // Resposta selecionada e incorreta é vermelha
        
        return "cursor-not-allowed opacity-60"; // Outras alternativas ficam desativadas
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-6 transition-all duration-300 hover:shadow-lg">
            <p className="font-semibold text-gray-800 mb-4">Questão {numero}: {exercicio.enunciado}</p>
            {exercicio.imagemUrl && <img src={exercicio.imagemUrl} alt={`Imagem da questão ${numero}`} className="mb-4 rounded-lg" />}
            <div className="space-y-3 mb-4">
                {exercicio.alternativas.map((alt, index) => (
                    <label key={index} className={`block p-3 border rounded-lg transition-colors ${getAlternativaClass(index)} ${!!resultado ? '' : 'cursor-pointer'}`}>
                        <input 
                            type="radio" 
                            name={`questao-${exercicio._id}`} 
                            className="mr-3"
                            onChange={() => setRespostaSelecionada(index)}
                            disabled={!!resultado || isSubmitting} // Desabilita durante o envio
                        />
                        {alt}
                    </label>
                ))}
            </div>
            {feedbackMessage && (
                <p className={`mb-4 text-center font-bold ${resultado === 'correto' ? 'text-green-600' : 'text-red-600'}`}>
                    {feedbackMessage}
                </p>
            )}
            <button 
                onClick={handleCheckResposta} 
                disabled={respostaSelecionada === null || !!resultado || isSubmitting} // Desabilita o botão
                className="w-full bg-[#9B4DFF] text-white font-semibold py-2 rounded-lg hover:bg-[#7B2CDB] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                {isSubmitting ? 'Verificando...' : 'Verificar Resposta'}
            </button>
            
            {/* Componente de animação de recompensa */}
            <RewardAnimation 
                isVisible={mostrarAnimacao} 
                onComplete={() => setMostrarAnimacao(false)} 
            />
        </div>
    );
};

// Componente para uma videoaula
const VideoAula = ({ aula, subtopicoId, token, onMostrarFlashcards }) => {
    const { dispatch: userDispatch } = useUser(); // Get dispatch from global context
    const [visto, setVisto] = useState(false);
    const [marcando, setMarcando] = useState(false);
    const [modoTelaCheia, setModoTelaCheia] = useState(false);
    const [mostrarNotificacao, setMostrarNotificacao] = useState(false);

    // Verificar se a aula já foi marcada como vista
    useEffect(() => {
        const verificarConteudoVisto = async () => {
            if (!token || !aula._id || !subtopicoId) return;

            try {
                const config = {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                };

                const response = await api.get(`/api/conteudo-visto/${aula._id}/${subtopicoId}`, config);
                setVisto(response.data.visto);
            } catch (error) {
                console.error('Erro ao verificar conteúdo visto:', error);
            }
        };

        verificarConteudoVisto();
    }, [token, aula._id, subtopicoId]);

    // Handle ESC key to exit fullscreen
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && modoTelaCheia) {
                setModoTelaCheia(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [modoTelaCheia]);

    const handleMarcarVisto = async () => {
        if (visto || marcando) return;

        setMarcando(true);

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            const data = {
                videoaulaId: aula._id,
                subtopicoId: subtopicoId
            };

            await api.post('/api/conteudo-visto', data, config);
            setVisto(true);

        } catch (error) {
            console.error('Erro ao marcar conteúdo como visto:', error);
        } finally {
            setMarcando(false);
        }
    };

    const handleTelaCheia = () => {
        const novoModoTelaCheia = !modoTelaCheia;
        setModoTelaCheia(novoModoTelaCheia);

        if (novoModoTelaCheia) {
            setMostrarNotificacao(true);
            // Hide notification after 3 seconds
            setTimeout(() => setMostrarNotificacao(false), 3000);
        }
    };

    return (
        <div className={`bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-6 transition-all duration-300 hover:shadow-lg ${modoTelaCheia ? 'fixed inset-0 z-50 bg-black p-0' : ''}`}>
            {modoTelaCheia && (
                <div className="flex justify-between items-start mb-4 p-4 bg-white">
                    <h3 className="font-semibold text-xl">{aula.titulo}</h3>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setModoTelaCheia(false)}
                            className="px-3 py-1 rounded-full text-sm font-medium transition-colors bg-violet-100 text-violet-800 hover:bg-[#9B4DFF] hover:text-white"
                        >
                            Marcar como visto
                        </button>
                    </div>
                </div>
            )}
            {!modoTelaCheia && (
                <div className="flex justify-between items-start mb-4">
                    <h3 className="font-semibold text-lg text-gray-800">{aula.titulo}</h3>
                    <div className="flex space-x-2">
                        <button
                            onClick={handleMarcarVisto}
                            disabled={visto || marcando}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                visto
                                    ? 'bg-green-100 text-green-800'
                                    : marcando
                                        ? 'bg-gray-200 text-gray-600'
                                        : 'bg-violet-100 text-violet-800 hover:bg-[#9B4DFF] hover:text-white'
                            }`}
                        >
                            {visto ? 'Visto' : marcando ? 'Marcando...' : 'Marcar como visto'}
                        </button>
                    </div>
                </div>
            )}
            <div className={`${modoTelaCheia ? 'relative h-[calc(100vh-70px)]' : 'aspect-video bg-black rounded-lg overflow-hidden'}`}>
                <iframe
                    className={`w-full h-full`}
                    src={aula.url}
                    title={aula.titulo}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                ></iframe>
            </div>

            {/* Notification for ESC key */}
            {mostrarNotificacao && (
                <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50">
                    Pressione a tecla ESC para sair
                </div>
            )}
        </div>
    );
};

// Componente principal da página
const SubtopicoPage = ({ subtopicoId, subtopicoNome, onVoltar, isAdmin: initialIsAdmin }) => {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [itemAtivo, setItemAtivo] = useState(null);
    const [token, setToken] = useState(null); // Estado para armazenar o token
    const [isAdmin, setIsAdmin] = useState(initialIsAdmin || false); // Estado para verificar se o usuário é administrador
    const [barraLateralMinimizada, setBarraLateralMinimizada] = useState(false);
    const [videoIndice, setVideoIndice] = useState(0); // Para controle dos botões Próximo/Anterior


    // State for PDF practice pages
    const [pdfPracticePages, setPdfPracticePages] = useState([]);

    // State for PDF contribution form
    const [newPdfTitle, setNewPdfTitle] = useState('');
    const [newGoogleDriveLink, setNewGoogleDriveLink] = useState('');
    const [selectedVideoAulas, setSelectedVideoAulas] = useState([]);
    const [submittingPdf, setSubmittingPdf] = useState(false);
    const [selectedFilterVideo, setSelectedFilterVideo] = useState('');
    const [showPdfForm, setShowPdfForm] = useState(false); // State to control PDF form visibility

    // State for filtered PDF pages
    const [filteredPdfPracticePages, setFilteredPdfPracticePages] = useState([]);

    // Toggle function for PDF form visibility
    const togglePdfForm = () => {
        setShowPdfForm(!showPdfForm);
    };

    useEffect(() => {
        // Obter o token do localStorage no carregamento do componente
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        if (userInfo && userInfo.token) {
            setToken(userInfo.token);
            // Verificar se o usuário é administrador baseado nas informações do localStorage ou na prop inicial
            setIsAdmin(initialIsAdmin || userInfo.isAdmin || false);

            // Fazer uma verificação atualizada com o backend para garantir que o status de admin esteja correto
            const checkAdminStatus = async () => {
                try {
                    const config = {
                        headers: { Authorization: `Bearer ${userInfo.token}` }
                    };
                    const response = await api.get('/api/admin/check-admin', config);
                    setIsAdmin(response.data.isAdmin || false);
                } catch (error) {
                    console.error('Erro ao verificar status de administrador:', error);
                    // Manter o status anterior em caso de erro
                }
            };

            // Executar verificação de administração em segundo plano
            checkAdminStatus();
        } else {
            setError('Usuário não autenticado. Por favor, faça login.');
            setLoading(false);
            return;
        }

        if (!subtopicoId) {
            setError("ID do subtópico não fornecido.");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            console.log('Iniciando fetch de dados para subtopicoId:', subtopicoId);
            setLoading(true);
            setError(null);
            try {
                // Configura o cabeçalho de autorização usando o token do estado
                const config = { headers: { Authorization: `Bearer ${token}` } };
                console.log('Token de autorização:', token);

                // Busca os detalhes do subtópico (vídeos, etc.)
                console.log('Fazendo requisição para:', `/api/materias/subtopicos/${subtopicoId}`);
                const subtopicoRes = await api.get(`/api/materias/subtopicos/${subtopicoId}`, config);
                console.log('Resposta do subtópico:', subtopicoRes.data);
                setDados(subtopicoRes.data);

                // Verifica se videoaulas existem
                console.log('Videoaulas no subtópico:', subtopicoRes.data.subtopico?.videoaulas);

                // Busca as páginas de prática PDF para este subtópico
                console.log('Fazendo requisição para PDF practice pages:', `/api/pdf-practice-pages/subtopico/${subtopicoId}`);
                const pdfPracticePagesRes = await api.get(`/api/pdf-practice-pages/subtopico/${subtopicoId}`, config);
                console.log('Resposta de PDF practice pages:', pdfPracticePagesRes.data);
                setPdfPracticePages(pdfPracticePagesRes.data.data || []);

                // Define o item ativo inicial
                if (subtopicoRes.data.subtopico?.videoaulas && subtopicoRes.data.subtopico.videoaulas.length > 0) {
                    console.log('Definindo videoaula ativa:', subtopicoRes.data.subtopico.videoaulas[0]);
                    setItemAtivo({ ...subtopicoRes.data.subtopico.videoaulas[0], tipo: 'videoaula' });
                    setVideoIndice(0); // Define o índice do vídeo como o primeiro
                } else {
                    console.log('Nenhuma videoaula encontrada, definindo exercicios como ativo');
                    setItemAtivo({ _id: 'exercicios', tipo: 'exercicios' });
                    setVideoIndice(null); // Nenhum vídeo ativo
                }

            } catch (err) {
                console.error('Erro detalhado na função fetchData:', err);
                console.error('Mensagem do erro:', err.message);
                console.error('Código do erro:', err.code);
                if (err.response) {
                    console.error('Status do erro:', err.response.status);
                    console.error('Dados do erro:', err.response.data);
                    console.error('Headers do erro:', err.response.headers);
                }
                setError("Ocorreu um erro ao carregar o conteúdo. Tente novamente.");
            } finally {
                setLoading(false);
            }
        };

        if (token) { // Só faz o fetch se o token estiver disponível
            fetchData();
        }
    }, [subtopicoId, token, initialIsAdmin]); // Adicionado 'token' e 'initialIsAdmin' às dependências

    // Efeito para filtrar as páginas de prática PDF
    useEffect(() => {
        if (selectedFilterVideo) {
            // Filtrar páginas de prática que estão associadas ao vídeo selecionado
            const filtered = pdfPracticePages.filter(page =>
                page.videoAulaIds && page.videoAulaIds.includes(selectedFilterVideo)
            );
            setFilteredPdfPracticePages(filtered);
        } else {
            setFilteredPdfPracticePages(pdfPracticePages);
        }
    }, [selectedFilterVideo, pdfPracticePages]); // Adicionado pdfPracticePages às dependências

    // Função para selecionar/desselecionar videoaulas
    const handleSelectVideoAula = (aulaId) => {
        if (selectedVideoAulas.includes(aulaId)) {
            setSelectedVideoAulas(selectedVideoAulas.filter(id => id !== aulaId));
        } else {
            setSelectedVideoAulas([...selectedVideoAulas, aulaId]);
        }
    };

    // Função para validar se é um link do Google Drive
    const isValidGoogleDriveLink = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname === 'drive.google.com' || urlObj.hostname === 'docs.google.com';
        } catch (e) {
            return false;
        }
    };

    // Função para lidar com a mudança de arquivo
    const handleGoogleDriveLinkChange = (e) => {
        setNewGoogleDriveLink(e.target.value);
    };

    // Função para remover um PDF específico de uma página de prática
    const handleRemovePdfFile = async (pageId, pdfFileId) => {
        if (!window.confirm('Tem certeza que deseja remover este PDF? Esta ação não pode ser desfeita.')) {
            return;
        }

        try {
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            };

            await api.delete(`/api/pdf-practice-pages/${pageId}/pdf/${pdfFileId}`, config);

            // Atualizar a lista local de páginas de prática
            setPdfPracticePages(prevPages =>
                prevPages.map(page => {
                    if (page._id === pageId) {
                        const updatedPdfFiles = page.pdfFiles.filter(pdfFile => pdfFile._id !== pdfFileId);
                        return { ...page, pdfFiles: updatedPdfFiles };
                    }
                    return page;
                })
            );

            // Atualizar também a lista filtrada
            setFilteredPdfPracticePages(prevPages =>
                prevPages.map(page => {
                    if (page._id === pageId) {
                        const updatedPdfFiles = page.pdfFiles.filter(pdfFile => pdfFile._id !== pdfFileId);
                        return { ...page, pdfFiles: updatedPdfFiles };
                    }
                    return page;
                })
            );

            alert('PDF removido com sucesso!');
        } catch (error) {
            console.error('Erro ao remover PDF:', error);
            alert('Erro ao remover PDF. Tente novamente.');
        }
    };

    // Função para remover uma página de prática completa
    const handleRemovePdfPage = async (pageId) => {
        if (!window.confirm('Tem certeza que deseja remover esta página de prática completa? Esta ação não pode ser desfeita.')) {
            return;
        }

        try {
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            };

            await api.delete(`/api/pdf-practice-pages/${pageId}`, config);

            // Atualizar a lista local de páginas de prática
            setPdfPracticePages(prevPages =>
                prevPages.filter(page => page._id !== pageId)
            );

            // Atualizar também a lista filtrada
            setFilteredPdfPracticePages(prevPages =>
                prevPages.filter(page => page._id !== pageId)
            );

            alert('Página de prática removida com sucesso!');
        } catch (error) {
            console.error('Erro ao remover página de prática:', error);
            alert('Erro ao remover página de prática. Tente novamente.');
        }
    };

    // Função para enviar o PDF de exercícios
    const handleSubmitPdf = async (e) => {
        e.preventDefault();

        if (!newGoogleDriveLink) {
            alert('Por favor, insira um link do Google Drive.');
            return;
        }

        if (!isValidGoogleDriveLink(newGoogleDriveLink)) {
            alert('Por favor, insira um link válido do Google Drive (https://drive.google.com/... ou https://docs.google.com/...).');
            return;
        }

        if (selectedVideoAulas.length === 0) {
            alert('Por favor, selecione pelo menos uma Videoaula relacionada.');
            return;
        }

        setSubmittingPdf(true);

        try {
            const linkData = {
                title: newPdfTitle,
                googleDriveLink: newGoogleDriveLink,
                subtopicoId: subtopicoId,
                videoAulaIds: selectedVideoAulas
            };

            // Enviar para a API para adicionar diretamente à lista de páginas de prática (sem aprovação)
            const response = await api.post('/api/pdf-practice-pages/direct', linkData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Adicionar o novo PDF à lista local imediatamente
            // A resposta do backend deve conter a página de prática atualizada com o novo PDF
            const updatedPage = response.data.data;

            // Atualizar a lista de páginas de prática, substituindo a página existente ou adicionando uma nova
            const existingPageIndex = pdfPracticePages.findIndex(page => page._id === updatedPage._id);

            if (existingPageIndex !== -1) {
                // Se a página já existir, atualize-a
                const updatedPages = [...pdfPracticePages];
                updatedPages[existingPageIndex] = updatedPage;
                setPdfPracticePages(updatedPages);
            } else {
                // Se for uma nova página, adicione-a
                setPdfPracticePages(prevPages => [...prevPages, updatedPage]);
            }

            // Limpar o formulário
            setNewPdfTitle('');
            setNewGoogleDriveLink('');
            setSelectedVideoAulas([]);

            alert('PDF de exercícios adicionado com sucesso e já disponível para a comunidade!');

        } catch (error) {
            console.error('Erro ao enviar o link do Google Drive:', error);
            alert('Erro ao enviar o link do Google Drive. Tente novamente.');
        } finally {
            setSubmittingPdf(false);
        }
    };






    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">A carregar conteúdo...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg hover:bg-[#7B2CDB] transition-colors"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    if (!dados) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-500">Nenhum dado encontrado.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F6FC]">
            <div className="container mx-auto p-4 sm:p-6 md:p-8">
                <header className="flex items-center mb-8">
                    <button onClick={onVoltar} className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                        Voltar
                    </button>
                </header>

                <div className="flex flex-col lg:flex-row gap-8">
                    <aside className={`${barraLateralMinimizada ? 'w-16' : 'w-full lg:w-1/3 xl:w-1/4'} flex-shrink-0 transition-all duration-300`}>
                        <div className="bg-white rounded-xl shadow-md border border-[#E0CCFF] p-4 sticky top-8">
                            <div className="flex justify-between items-center mb-4">
                                {!barraLateralMinimizada && (
                                    <div>
                                        <p className="text-sm text-gray-500">{dados.materia.nome} &gt; {dados.topico.nome}</p>
                                        <h2 className="text-xl font-bold text-[#1E1E1E]">{dados.subtopico.nome}</h2>
                                    </div>
                                )}
                                <button
                                    onClick={() => setBarraLateralMinimizada(!barraLateralMinimizada)}
                                    className="text-gray-500 hover:text-gray-700 ml-auto"
                                >
                                    {barraLateralMinimizada ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-violet-300 scrollbar-track-violet-100">
                                <ul className="space-y-2">
                                    {!barraLateralMinimizada && dados.subtopico.videoaulas.map(aula => (
                                        <li
                                            key={aula._id}
                                            onClick={() => {
                                                setItemAtivo({ ...aula, tipo: 'videoaula' });
                                                setVideoIndice(dados.subtopico.videoaulas.findIndex(video => video._id === aula._id));
                                            }}
                                            className={`p-3 rounded-md flex items-center gap-3 cursor-pointer transition-colors duration-200 ${itemAtivo?._id === aula._id ? 'bg-[#F3E8FF] border-l-4 border-[#9B4DFF]' : 'hover:bg-violet-100'}`}
                                        >
                                            <span className={`font-medium ${itemAtivo?._id === aula._id ? 'text-[#7B2CDB] font-semibold' : 'text-gray-600'}`}>
                                                {aula.titulo}
                                            </span>
                                        </li>
                                    ))}
                                    {!barraLateralMinimizada && (
                                        <li
                                            onClick={() => {
                                                setItemAtivo({ _id: 'exercicios', tipo: 'exercicios' });
                                                setVideoIndice(null); // Não há índice de vídeo quando visualizando exercícios
                                            }}
                                            className={`p-3 rounded-md flex items-center gap-3 cursor-pointer transition-colors duration-200 ${itemAtivo?._id === 'exercicios' ? 'bg-[#F3E8FF] border-l-4 border-[#9B4DFF]' : 'hover:bg-violet-100'}`}
                                        >
                                            <span className={`font-medium ${itemAtivo?._id === 'exercicios' ? 'text-[#7B2CDB] font-semibold' : 'text-gray-600'}`}>
                                                Pratique com Exercícios
                                            </span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </aside>

                    <main className="w-full lg:w-2/3 xl:w-3/4">
                        {console.log('itemAtivo:', itemAtivo, 'dados:', dados)}
                        {itemAtivo && itemAtivo.tipo === 'videoaula' ? (
                            <div>
                                {console.log('Renderizando VideoAula com aula:', itemAtivo)}
                                <VideoAula
                                    aula={itemAtivo}
                                    subtopicoId={subtopicoId}
                                    token={token}
                                />

                                {/* Botões de navegação entre vídeos */}
                                <div className="flex justify-between mt-4 mb-6">
                                    <button
                                        onClick={() => {
                                            if (videoIndice > 0) {
                                                const novoIndice = videoIndice - 1;
                                                setItemAtivo({ ...dados.subtopico.videoaulas[novoIndice], tipo: 'videoaula' });
                                                setVideoIndice(novoIndice);
                                            }
                                        }}
                                        disabled={videoIndice === 0}
                                        className={`px-4 py-2 rounded-lg font-semibold ${
                                            videoIndice === 0
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-violet-600 text-white hover:bg-violet-700'
                                        }`}
                                    >
                                        &larr; Anterior
                                    </button>

                                    <button
                                        onClick={() => {
                                            if (videoIndice < dados.subtopico.videoaulas.length - 1) {
                                                const novoIndice = videoIndice + 1;
                                                setItemAtivo({ ...dados.subtopico.videoaulas[novoIndice], tipo: 'videoaula' });
                                                setVideoIndice(novoIndice);
                                            }
                                        }}
                                        disabled={videoIndice === dados.subtopico.videoaulas.length - 1}
                                        className={`px-4 py-2 rounded-lg font-semibold ${
                                            videoIndice === dados.subtopico.videoaulas.length - 1
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-violet-600 text-white hover:bg-violet-700'
                                        }`}
                                    >
                                        Próximo &rarr;
                                    </button>
                                </div>

                                <FlashcardsSubtopico subtopicoId={subtopicoId} videoaulaId={itemAtivo?.tipo === 'videoaula' ? itemAtivo._id : null} />
                            </div>
                        ) : (
                            <section>
                                <h2 className="text-2xl font-bold text-gray-800 mb-4">Pratique com Exercícios</h2>

                                {/* Botão para sugerir PDF (apenas para usuários logados) */}
                                <div className="mb-8">
                                    <button
                                        onClick={togglePdfForm}
                                        className="w-full bg-[#9B4DFF] text-white font-semibold py-3 rounded-lg hover:bg-[#7B2CDB] transition-colors mb-4"
                                    >
                                        Sugerir
                                    </button>

                                    {/* Formulário para sugerir PDF de exercícios */}
                                    {showPdfForm && (
                                        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-6">
                                            <h3 className="text-xl font-bold text-gray-800 mb-4">Sugerir PDF de Exercícios</h3>
                                            <form onSubmit={handleSubmitPdf}>
                                                <div className="mb-4">
                                                    <label className="block text-gray-700 font-medium mb-2">Título</label>
                                                    <input
                                                        type="text"
                                                        value={newPdfTitle}
                                                        onChange={(e) => setNewPdfTitle(e.target.value)}
                                                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#9B4DFF] focus:border-transparent"
                                                        placeholder="Título do PDF de exercícios"
                                                    />
                                                </div>
                                                <div className="mb-4">
                                                    <label className="block text-gray-700 font-medium mb-2">Link do Google Drive</label>
                                                    <input
                                                        type="text"
                                                        value={newGoogleDriveLink}
                                                        onChange={handleGoogleDriveLinkChange}
                                                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#9B4DFF] focus:border-transparent"
                                                        placeholder="Cole o link do Google Drive aqui"
                                                    />
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Exemplo: https://drive.google.com/file/d/... ou https://docs.google.com/...
                                                    </p>
                                                </div>
                                                <div className="mb-4">
                                                    <label className="block text-gray-700 font-medium mb-2">
                                                        Videoaulas Relacionadas
                                                    </label>
                                                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
                                                        {dados?.subtopico?.videoaulas?.map((aula) => (
                                                            <label key={aula._id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedVideoAulas.includes(aula._id)}
                                                                    onChange={() => handleSelectVideoAula(aula._id)}
                                                                    className="mr-2"
                                                                />
                                                                <span>{aula.titulo}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex space-x-3">
                                                    <button
                                                        type="submit"
                                                        disabled={submittingPdf}
                                                        className="flex-1 bg-[#9B4DFF] text-white font-semibold py-3 rounded-lg hover:bg-[#7B2CDB] transition-colors disabled:opacity-50"
                                                    >
                                                        {submittingPdf ? 'Enviando...' : 'Enviar Sugestão'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={togglePdfForm}
                                                        className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-300 transition-colors"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}

                                    {/* Exibir páginas de prática PDF existentes */}
                                    {pdfPracticePages.length > 0 && (
                                        <div className="mb-8">
                                            <div className="flex justify-between items-center mb-3">
                                                <h3 className="text-lg font-semibold text-gray-800">PDFs de Exercícios Disponíveis</h3>
                                                {/* Filtro por videoaula */}
                                                <div className="flex items-center space-x-2">
                                                    <label className="text-sm text-gray-600">Filtrar por:</label>
                                                    <select
                                                        value={selectedFilterVideo}
                                                        onChange={(e) => setSelectedFilterVideo(e.target.value)}
                                                        className="border rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-[#9B4DFF] focus:border-transparent"
                                                    >
                                                        <option value="">Todas as videoaulas</option>
                                                        {dados?.subtopico?.videoaulas?.map((aula) => (
                                                            <option key={aula._id} value={aula._id}>
                                                                {aula.titulo}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {filteredPdfPracticePages.map((page) => (
                                                    <div key={page._id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <h4 className="font-medium text-gray-800">{page.title}</h4>
                                                                {page.pdfFiles && page.pdfFiles.length > 0 && (
                                                                    <div className="mt-2">
                                                                        {page.pdfFiles.map((pdfFile, index) => (
                                                                            <div key={index} className="flex items-center justify-between mb-1">
                                                                                <a
                                                                                    href={pdfFile.googleDriveLink}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-[#9B4DFF] hover:text-[#7B2CDB] font-medium text-sm inline-flex items-center"
                                                                                >
                                                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                                    </svg>
                                                                                    Abrir PDF
                                                                                </a>
                                                                                {/* Botão de remoção para administradores */}
                                                                                {token && isAdmin && (
                                                                                    <button
                                                                                        onClick={() => handleRemovePdfFile(page._id, pdfFile._id)}
                                                                                        className="text-red-500 hover:text-red-700 ml-2"
                                                                                        title="Remover PDF"
                                                                                    >
                                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                                        </svg>
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {/* Exibir videoaulas associadas ao PDF */}
                                                                {page.videoAulaIds && page.videoAulaIds.length > 0 && (
                                                                    <div className="mt-2">
                                                                        <p className="text-xs text-gray-500">Vídeos associados:</p>
                                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                                            {page.videoAulaIds.map((videoId, index) => {
                                                                                const videoAula = dados?.subtopico?.videoaulas?.find(aula => aula._id === videoId);
                                                                                return videoAula ? (
                                                                                    <span
                                                                                        key={index}
                                                                                        className="bg-violet-100 text-violet-800 text-xs px-2 py-1 rounded"
                                                                                    >
                                                                                        {videoAula.titulo}
                                                                                    </span>
                                                                                ) : null;
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {page.createdBy && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        Contribuído por: {page.createdBy.nome}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {/* Botão de remoção para administradores */}
                                                            {token && isAdmin && (
                                                                <button
                                                                    onClick={() => handleRemovePdfPage(page._id)}
                                                                    className="text-red-500 hover:text-red-700"
                                                                    title="Remover página completa"
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </section>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default SubtopicoPage;
